import { assign, createMachine, fromPromise } from 'xstate'

import Api from '@/api/_legacy'
import { ApiDfmWithUsers } from '@/api/_legacy/generated-api'

import { setErrorsAction } from '../../common/set-errors-action'
import { Mutation } from '../../types'

const TENANT_ID = 'absa'

export type EditDfmState = {
  dfm: ApiDfmWithUsers
  errorMessages: string[]
  errors: { [key: string]: string }
  isSaveEnabled: boolean
  isSaveSuccess: boolean
  onSaveSuccess?: () => void
}

type InitEvent = {
  dfm: ApiDfmWithUsers
  onSave: () => void
  type: 'INIT'
}

type MutateEvent = {
  mutation: Mutation<ApiDfmWithUsers>
  type: 'MUTATE'
}

type SaveEvent = {
  dfm: ApiDfmWithUsers
  type: 'SAVE'
}

export type AddDfmEvent = InitEvent | MutateEvent | SaveEvent
export const editDfmMachine = createMachine(
  {
    types: {} as {
      context: EditDfmState
      events: AddDfmEvent
    },
    id: 'edit-dfm-machine',
    initial: 'idle',
    context: {
      dfm: {
        dfmId: '',
        dfmName: '',
        users: []
      },
      isSaveSuccess: false,
      isSaveEnabled: false,
      errors: {},
      errorMessages: []
    },
    states: {
      idle: {
        on: {
          INIT: {
            actions: ['setDfm'],
            target: 'dfmLoaded'
          }
        }
      },
      dfmLoaded: {
        on: {
          MUTATE: {
            actions: ['mutate', 'setDirty'],
            target: 'dfmLoaded'
          },
          SAVE: 'saving',
          INIT: {
            actions: ['setDfm'],
            target: 'dfmLoaded'
          }
        }
      },
      saving: {
        invoke: {
          id: 'saveDfm',
          src: 'saveDfm',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            actions: ['clearErrors', 'notifyParentOfSave'],
            target: 'dfmLoaded'
          },
          onError: {
            actions: ['setError'],
            target: 'dfmLoaded'
          }
        }
      }
    }
  },
  {
    actors: {
      saveDfm: fromPromise(async ({ input }: any) => {
        const { event } = input
        const dfm = event.dfm as ApiDfmWithUsers

        if (dfm.dfmName.length < 1) {
          throw {
            type: 'validation',
            errors: {
              dfmName: 'Dfm name must be supplied'
            }
          }
        }

        const response = await Api.tenant.putTenant(TENANT_ID, dfm)
        const { data } = response
        return data
      })
    },
    actions: {
      setDfm: assign(({ event }: any) => ({
        dfm: event.dfm,
        onSaveSuccess: event.onSave
      })),
      mutate: assign(({ context, event }: any) => {
        const dfm = { ...context.dfm }
        event.mutation(dfm)

        return {
          ...context,
          dfm
        }
      }),
      setDirty: assign(context => ({
        ...context,
        isSaveEnabled: true
      })),
      setError: assign(({ context, event }: any) => setErrorsAction(context, event)),
      clearErrors: assign(context => ({
        ...context,
        errors: {}
      })),
      notifyParentOfSave: assign(({ context }) => {
        if (context.onSaveSuccess) {
          context.onSaveSuccess()
        }
        return context
      })
    }
  }
)
